'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { User } from 'lucide-react';

import { Avatar, Button } from '@mokoboko/components';
import { ExpandableText } from './expandable-text';
import { TenantContactChips } from './tenant-contact-chips';
import { Tenant } from '@/state/tenant';
import { getTenantContactInfo, splitTenantContactInfo } from '@/helpers/tenant';
import { toPlainText } from '@/lib/plain-text';
import useAppearanceSettings from '@/state/appearance-settings';

// stopPropagation so the container's mouse-convenience onClick does not fire a
// second navigation on top of the anchor's own.
const TenantNameLink = ({ href, name }: { href: string; name: string }) => (
  <Link
    href={href}
    className="mb-focus-visible"
    onClick={(e) => e.stopPropagation()}
  >
    {name}
  </Link>
);

export const TenantAvatar = ({
  tenant,
}: {
  tenant: Pick<Tenant, 'logo' | 'name'>;
}) => (
  <Avatar
    src={tenant.logo ?? undefined}
    name={tenant.name}
    size="md"
    shape="circle"
    className="sm:h-12 sm:w-12 sm:min-w-[48px]"
  />
);

export const TenantDetails = ({
  tenant,
  onLoginClick,
}: {
  tenant: Tenant;
  onLoginClick: () => void;
}) => {
  const { name, description, logo } = tenant;
  const router = useRouter();
  const { data: appearanceSetting } = useAppearanceSettings();
  const generalSettings = appearanceSetting?.general;
  const isLogoVisible = !!generalSettings?.show_logo && !!logo;
  const isDescriptionVisible = !!(
    generalSettings?.show_description && description
  );

  const { social, details } = splitTenantContactInfo(
    getTenantContactInfo(tenant, generalSettings)
  );

  // Mouse-only convenience. The heading's link owns keyboard and assistive
  // access: a role="button" here would make the <h1> a presentational child
  // and drop it out of the accessibility tree entirely.
  const pressableProps = { onClick: () => router.push('/') };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div
          {...pressableProps}
          className="flex min-w-0 cursor-pointer items-center gap-3"
        >
          {isLogoVisible && <TenantAvatar tenant={tenant} />}
          <h1 className="heading-page min-w-0 text-wrap text-lg leading-tight tracking-[-0.01em] sm:text-xl sm:leading-tight">
            <TenantNameLink href="/" name={name} />
          </h1>
        </div>

        <Button
          variant="secondary-gray"
          size="sm"
          leadingIcon={<User size={16} />}
          onClick={onLoginClick}
          className="ml-auto shrink-0"
        >
          Login
        </Button>
      </div>

      {isDescriptionVisible && (
        <ExpandableText
          text={toPlainText(description)}
          className="text-sm leading-5 text-gray-700"
          containerClassName="mt-2"
          moreLabel="About"
          lessLabel="Show less"
        >
          {details.hasDetails && (
            <TenantContactChips contact={details} className="mt-3" />
          )}
        </ExpandableText>
      )}

      {social.hasDetails && (
        <TenantContactChips contact={social} className="mt-3" />
      )}
    </div>
  );
};
